import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Hand, Calendar, Webhook, Mail } from 'lucide-react';
import { theme } from '../../styles/theme';

export default function TriggerTypeSelector({ value, onChange }) {
  const { t } = useTranslation('campaigns');

  const TRIGGER_TYPES = [
    { value: 'manual', label: t('triggerType.manual'), description: t('triggerType.manualDesc'), icon: Hand },
    { value: 'scheduled', label: t('triggerType.scheduled'), description: t('triggerType.scheduledDesc'), icon: Calendar },
    { value: 'endpoint', label: t('triggerType.endpoint'), description: t('triggerType.endpointDesc'), icon: Webhook },
    { value: 'email', label: t('triggerType.email'), description: t('triggerType.emailDesc'), icon: Mail },
  ];

  return (
    <Container>
      <Label>{t('triggerType.label')}</Label>
      <CardGrid>
        {TRIGGER_TYPES.map(({ value: type, label, description, icon: Icon }) => (
          <Card
            key={type}
            $active={value === type}
            onClick={() => onChange(type)}
            role="radio"
            aria-checked={value === type}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(type);
              }
            }}
          >
            <IconWrapper $active={value === type}>
              <Icon size={20} />
            </IconWrapper>
            <CardContent>
              <CardLabel $active={value === type}>{label}</CardLabel>
              <CardDescription>{description}</CardDescription>
            </CardContent>
          </Card>
        ))}
      </CardGrid>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const Label = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing[3]};
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  border-radius: ${theme.radii.lg};
  border: 2px solid ${(p) => (p.$active ? theme.colors.primary : theme.colors.border)};
  background: ${(p) => (p.$active ? theme.colors.primaryLight : theme.colors.white)};
  cursor: pointer;
  transition: ${theme.transitions.fast};

  &:hover {
    border-color: ${(p) => (p.$active ? theme.colors.primary : theme.colors.borderHover)};
    box-shadow: ${theme.shadows.sm};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.radii.md};
  background: ${(p) => (p.$active ? theme.colors.primary : theme.colors.bgSecondary)};
  color: ${(p) => (p.$active ? theme.colors.white : theme.colors.textMuted)};
  flex-shrink: 0;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CardLabel = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${(p) => (p.$active ? theme.colors.primary : theme.colors.textPrimary)};
`;

const CardDescription = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;
